/**
 * D14 — canonical CRM contact CSV parser. ONE parser for both the file picker and the paste box.
 *
 * Safe by construction: strips a UTF-8 BOM, honours RFC-4180 quoting, ignores empty rows, validates
 * required fields, normalizes phone to E.164-ish, detects file-internal duplicates, and neutralizes
 * spreadsheet-formula-injection for any value that could later be re-exported. It NEVER trusts a
 * tenant/business identifier from the file — the server derives the owner from the authenticated session.
 */
export const CSV_MAX_FILE_BYTES = 2 * 1024 * 1024;   // 2 MB
export const CSV_MAX_ROWS = 5000;
export const CSV_REQUIRED_HEADERS = ['name (or first_name + last_name)', 'phone OR email'];
export const CSV_OPTIONAL_HEADERS = ['email', 'phone'];
export const CSV_TEMPLATE = 'name,phone,email\nJane Doe,+15551234567,jane@example.com';
// Columns that must never be honoured from a file (tenant scope is server-authoritative).
const IGNORED_TENANT_HEADERS = ['tenant_id', 'business_id', 'brand_id', 'customer_id', 'owner', 'firebase_uid'];

// Neutralize leading formula characters so a value can never be interpreted as a spreadsheet formula.
function deFormula(v) {
  const s = String(v == null ? '' : v);
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
}
// RFC-4180-ish single-line splitter honouring double-quoted fields and "" escapes.
function splitCsvLine(line) {
  const out = []; let cur = ''; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ',') { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}
function normPhone(p) {
  const t = String(p || '').trim();
  if (!t) return '';
  return t.startsWith('+') ? t : `+${t.replace(/\D/g, '')}`;
}
function isEmail(e) { return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(e || '').trim()); }

/**
 * @returns {{rows:Array, invalid:Array<{row:number,reason:string}>, duplicatesInFile:number,
 *            total:number, headerDetected:boolean, ignoredColumns:string[]}}
 */
export function parseContactsCsv(text) {
  const clean = String(text || '').replace(/^﻿/, ''); // strip BOM
  const lines = clean.split(/\r\n|\r|\n/).filter((l) => l.trim().length > 0);
  const rows = []; const invalid = []; const seen = new Set();
  let duplicatesInFile = 0; let ignoredColumns = [];
  if (lines.length === 0) return { rows, invalid, duplicatesInFile: 0, total: 0, headerDetected: false, ignoredColumns };

  const firstCells = splitCsvLine(lines[0]).map((c) => c.toLowerCase());
  const headerDetected = firstCells.some((c) =>
    ['name', 'first_name', 'last_name', 'phone', 'phone_number', 'email'].includes(c));
  let idx = { name: 0, first: -1, last: -1, phone: 1, email: 2 };
  let start = 0;
  if (headerDetected) {
    start = 1;
    ignoredColumns = firstCells.filter((c) => IGNORED_TENANT_HEADERS.includes(c));
    const pi = firstCells.indexOf('phone');
    idx = {
      name: firstCells.indexOf('name'),
      first: firstCells.indexOf('first_name'),
      last: firstCells.indexOf('last_name'),
      phone: pi >= 0 ? pi : firstCells.indexOf('phone_number'),
      email: firstCells.indexOf('email'),
    };
  }

  for (let i = start; i < lines.length; i++) {
    const rowNo = i + 1;
    const cells = splitCsvLine(lines[i]);
    let name = idx.name >= 0 ? (cells[idx.name] || '') : '';
    const first = idx.first >= 0 ? (cells[idx.first] || '') : '';
    const last = idx.last >= 0 ? (cells[idx.last] || '') : '';
    if (!name && (first || last)) name = `${first} ${last}`.trim();
    if (!name && !headerDetected) name = cells[0] || '';
    name = name.trim();
    const phone = normPhone(idx.phone >= 0 ? cells[idx.phone] : '');
    const email = String((idx.email >= 0 ? cells[idx.email] : '') || '').trim();

    if (!name) { invalid.push({ row: rowNo, reason: 'missing name' }); continue; }
    if (!phone && !email) { invalid.push({ row: rowNo, reason: 'missing phone and email' }); continue; }
    if (email && !isEmail(email)) { invalid.push({ row: rowNo, reason: 'invalid email' }); continue; }

    const key = `${phone || ''}|${email.toLowerCase()}`;
    if (seen.has(key)) { duplicatesInFile++; continue; }
    seen.add(key);
    const parts = name.split(/\s+/);
    rows.push({
      first_name: deFormula(parts[0] || ''),
      last_name: deFormula(parts.slice(1).join(' ') || ''),
      phone_number: phone,
      email,
    });
  }
  return { rows, invalid, duplicatesInFile, total: lines.length - (headerDetected ? 1 : 0), headerDetected, ignoredColumns };
}
